import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { getStockLevelCounts, getStockLevelStyle } from '../utils/stockUtils';
import './AdminDashboard.css';

function AdminDashboard({ user, onLogout }) {
  const isHindi = user?.language === 'hi';
  const t = (en, hi) => (isHindi ? hi : en);
  const [activeTab, setActiveTab] = useState('stock');
  const [transactions, setTransactions] = useState([]);
  const [yarnTypes, setYarnTypes] = useState([]);
  const [unitNumbers, setUnitNumbers] = useState([]);
  const [stock, setStock] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [newYarnType, setNewYarnType] = useState('');
  const [newUnitNumber, setNewUnitNumber] = useState('');
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user', language: 'en' });
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [selectedYarnType, setSelectedYarnType] = useState('all');
  const [stockSearch, setStockSearch] = useState('');
  const [stockSort, setStockSort] = useState('none');
  const [showTotals, setShowTotals] = useState(false);
  const [yarnTypeSearch, setYarnTypeSearch] = useState('');
  const [yarnTypeSort, setYarnTypeSort] = useState('az');
  const [unitNumberSearch, setUnitNumberSearch] = useState('');
  const [unitNumberSort, setUnitNumberSort] = useState('az');
  const [editingYarnType, setEditingYarnType] = useState(null);
  const [editingUnitNumber, setEditingUnitNumber] = useState(null);
  const [editingStockNote, setEditingStockNote] = useState(null);
  const [success, setSuccess] = useState('');
  const [viewingNote, setViewingNote] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsPageSize, setTransactionsPageSize] = useState(25);
  const [settingsTab, setSettingsTab] = useState('system');
  const DEFAULT_SETTINGS = {
    stock_threshold_critical: '50',
    stock_threshold_low: '100',
    stock_threshold_medium: '200',
    color_indicators_enabled: 'true'
  };
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedSettings, setSavedSettings] = useState(DEFAULT_SETTINGS);
  const [formData, setFormData] = useState({
    chemical_type_id: '',
    unit_number_id: '',
    type: 'in',
    quantity: '',
    bags: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    loadData();
  }, [activeTab, selectedYarnType, settingsTab]);

  useEffect(() => {
    if (activeTab === 'transactions') {
      setTransactionsPage(1);
    }
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const promises = [
        api.getTransactions(),
        api.getChemicalTypes(),
        api.getUnitNumbers(),
        api.getStock(selectedYarnType === 'all' ? null : selectedYarnType),
        api.getSettings(),
      ];
      
      let usersIndex = null;
      if (activeTab === 'users' || (activeTab === 'settings' && settingsTab === 'users')) {
        usersIndex = promises.push(api.getUsers()) - 1;
      }

      const results = await Promise.all(promises);
      setTransactions(results[0]);
      setYarnTypes(results[1]);
      setUnitNumbers(results[2]);
      setStock(results[3]);
      setSettings(results[4]);
      setSavedSettings(results[4]);
      
      if (usersIndex !== null) {
        setUsers(results[usersIndex]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddYarnType = async (e) => {
    e.preventDefault();
    if (!newYarnType.trim()) return;

    try {
      await api.addChemicalType(newYarnType);
      setNewYarnType('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteYarnType = async (id) => {
    try {
      await api.deleteChemicalType(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditYarnType = (yarnType) => {
    setEditingYarnType({ id: yarnType.id, name: yarnType.name });
  };

  const handleUpdateYarnType = async (e) => {
    e.preventDefault();
    if (!editingYarnType || !editingYarnType.name.trim()) return;

    try {
      await api.updateChemicalType(editingYarnType.id, editingYarnType.name);
      setEditingYarnType(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUnitNumber = async (e) => {
    e.preventDefault();
    if (!newUnitNumber.trim()) return;

    try {
      await api.addUnitNumber(newUnitNumber);
      setNewUnitNumber('');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUnitNumber = async (id) => {
    try {
      await api.deleteUnitNumber(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditUnitNumber = (unitNumber) => {
    setEditingUnitNumber({ id: unitNumber.id, name: unitNumber.name });
  };

  const handleUpdateUnitNumber = async (e) => {
    e.preventDefault();
    if (!editingUnitNumber || !editingUnitNumber.name.trim()) return;

    try {
      await api.updateUnitNumber(editingUnitNumber.id, editingUnitNumber.name);
      setEditingUnitNumber(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.password.trim()) return;

    try {
      await api.addUser(newUser.username, newUser.password, newUser.role, newUser.language || 'en');
      setNewUser({ username: '', password: '', role: 'user', language: 'en' });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditUser = (usr) => {
    setEditingUser({
      id: usr.id,
      username: usr.username,
      password: '', // Leave empty, will only update if filled
      role: usr.role,
      language: usr.language || 'en',
    });
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      await api.updateUser(
        editingUser.id,
        editingUser.username,
        editingUser.password, // Can be empty
        editingUser.role,
        editingUser.language || 'en'
      );
      setEditingUser(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      await api.deleteUser(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUnlockUser = async (username) => {
    try {
      await api.unlockUser(username);
      setSuccess(t(`User "${username}" unlocked`, `यूज़र "${username}" अनलॉक किया गया`));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTimezoneChange = async (id, timezone) => {
    try {
      await api.updateUserTimezone(id, timezone);
      setSuccess(t('Timezone updated successfully', 'टाइमज़ोन सफलतापूर्वक अपडेट हुआ'));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLanguageChange = async (id, language) => {
    try {
      await api.updateUserLanguage(id, language);
      setSuccess(t('Language updated successfully', 'भाषा सफलतापूर्वक अपडेट हुई'));
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const parseUtcTimestamp = (value) => {
    if (!value) return null;
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const hasTz = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
    return new Date(hasTz ? normalized : `${normalized}Z`);
  };

  const getCurrentTimeZone = () => (
    user?.timezone || (user?.username === 'admin' ? 'America/Chicago' : 'Asia/Kolkata')
  );

  const formatDateTime = (value, timeZone) => {
    if (!value) return '-';
    const date = parseUtcTimestamp(value) || new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  const formatDateOnly = (value, timeZone) => {
    if (!value) return '-';
    const date = new Date(value.includes('T') ? value : `${value}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      dateStyle: 'medium',
    }).format(date);
  };

  const renderSettingsNav = () => (
    <nav className="settings-nav">
      <button
        type="button"
        className={`settings-nav-item ${settingsTab === 'yarnTypes' ? 'active' : ''}`}
        onClick={() => setSettingsTab('yarnTypes')}
      >
        {t('Manage Chemical Types', 'रसायन प्रकार प्रबंधित करें')}
      </button>
      <button
        type="button"
        className={`settings-nav-item ${settingsTab === 'unitNumbers' ? 'active' : ''}`}
        onClick={() => setSettingsTab('unitNumbers')}
      >
        {t('Manage Unit Numbers', 'यूनिट नंबर प्रबंधित करें')}
      </button>
      <button
        type="button"
        className={`settings-nav-item ${settingsTab === 'users' ? 'active' : ''}`}
        onClick={() => setSettingsTab('users')}
      >
        {t('Manage Users', 'उपयोगकर्ता प्रबंधित करें')}
      </button>
      <button
        type="button"
        className={`settings-nav-item ${settingsTab === 'system' ? 'active' : ''}`}
        onClick={() => setSettingsTab('system')}
      >
        {t('System', 'सिस्टम')}
      </button>
    </nav>
  );

  const handleDeleteTransaction = async (id) => {
    try {
      await api.deleteTransaction(id);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const openConfirmDialog = (payload) => {
    setConfirmDialog(payload);
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(null);
  };

  const executeConfirmAction = async () => {
    if (!confirmDialog) return;

    try {
      if (confirmDialog.type === 'yarnType') {
        await handleDeleteYarnType(confirmDialog.id);
      } else if (confirmDialog.type === 'unitNumber') {
        await handleDeleteUnitNumber(confirmDialog.id);
      } else if (confirmDialog.type === 'user') {
        await handleDeleteUser(confirmDialog.id);
      } else if (confirmDialog.type === 'transaction') {
        await handleDeleteTransaction(confirmDialog.id);
      } else if (confirmDialog.type === 'unlock') {
        await handleUnlockUser(confirmDialog.id);
      } else if (confirmDialog.type === 'transaction-confirm') {
        try {
          await api.confirmHoldTransaction(confirmDialog.id);
          setTransactions((prev) =>
            prev.map((txn) => (txn.id === confirmDialog.id ? { ...txn, type: 'out' } : txn))
          );
          setSuccess(t('Hold confirmed.', 'होल्ड पुष्टि हो गई।'));
          loadData();
        } catch (err) {
          setError(err.message);
        }
      } else if (confirmDialog.type === 'transaction-cancel') {
        try {
          await api.cancelHoldTransaction(confirmDialog.id);
          setTransactions((prev) => prev.filter((txn) => txn.id !== confirmDialog.id));
          setSuccess(t('Hold cancelled.', 'होल्ड रद्द किया गया।'));
          loadData();
        } catch (err) {
          setError(err.message);
        }
      }
    } finally {
      closeConfirmDialog();
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction({
      ...transaction,
      date: transaction.date.split('T')[0],
    });
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (!editingTransaction) return;

    try {
      await api.updateTransaction(editingTransaction.id, {
        chemical_type_id: editingTransaction.chemical_type_id || null,
        unit_number_id: parseInt(editingTransaction.unit_number_id, 10),
        type: editingTransaction.type,
        quantity: parseFloat(editingTransaction.quantity),
        bags: parseInt(editingTransaction.bags, 10),
        date: editingTransaction.date,
        notes: editingTransaction.notes || '',
      });
      setEditingTransaction(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStockNote = (item) => {
    setEditingStockNote({
      chemical_type_id: item.chemical_type_id,
      chemical_type_name: item.chemical_type_name,
      notes: item.stock_notes || '',
    });
  };

  const handleSaveStockNote = async (e) => {
    e.preventDefault();
    if (!editingStockNote) return;

    try {
      await api.saveStockNote(editingStockNote.chemical_type_id, editingStockNote.notes);
      setEditingStockNote(null);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.addTransaction({
        ...formData,
        unit_number_id: parseInt(formData.unit_number_id, 10),
        quantity: parseFloat(formData.quantity),
        bags: parseInt(formData.bags, 10),
      });
      setSuccess(t('Transaction added successfully!', 'लेनदेन सफलतापूर्वक जोड़ा गया!'));
      setFormData({
        chemical_type_id: '',
        unit_number_id: '',
        type: 'in',
        quantity: '',
        bags: '',
        date: new Date().toISOString().split('T')[0],
        notes: '',
      });
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportStock = (stockData) => {
    if (!stockData || stockData.length === 0) {
      setError(t('No stock data to export', 'निर्यात के लिए कोई स्टॉक डेटा नहीं है'));
      return;
    }

    // Get yarn type name for filename
    const yarnTypeName = selectedYarnType === 'all' 
      ? 'All_Types' 
      : yarnTypes.find(yt => yt.id === parseInt(selectedYarnType))?.name || 'Stock';

    // Create CSV content
    const headers = [
      t('Chemical Type', 'रसायन प्रकार'),
      t('Total In (Kg)', 'कुल इन (किग्रा)'),
      t('Total Out (Kg)', 'कुल आउट (किग्रा)'),
      t('Total Barrels In', 'कुल बैरल इन'),
      t('Total Barrels Out', 'कुल बैरल आउट'),
      t('Current Stock (Kg)', 'वर्तमान स्टॉक (किग्रा)'),
      t('Current Barrels', 'वर्तमान बैरल'),
      t('Notes', 'नोट्स'),
    ];
    const csvContent = [
      headers.join(','),
      ...stockData.map(item => [
        `"${item.chemical_type_name || '-'}"`,
        item.total_in,
        item.total_out,
        item.total_bags_in,
        item.total_bags_out,
        item.current_stock,
        item.current_bags,
        `"${item.stock_notes || ''}"`
      ].join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `Stock_${yarnTypeName}_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccess(t('Stock data exported successfully!', 'स्टॉक डेटा सफलतापूर्वक निर्यात हुआ!'));
  };

  const yarnTypeUsage = transactions.reduce((acc, tx) => {
    if (tx.chemical_type_id) {
      const key = String(tx.chemical_type_id);
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});

  const unitNumberUsage = transactions.reduce((acc, tx) => {
    if (tx.unit_number_id) {
      const key = String(tx.unit_number_id);
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});

  const filteredStock = stock
    .filter((item) => {
      const query = stockSearch.toLowerCase().trim();
      if (!query) return true;
      return (item.chemical_type_name || '').toLowerCase().includes(query);
    })
    .sort((a, b) => {
      if (stockSort === 'high') {
        return parseFloat(b.current_stock) - parseFloat(a.current_stock);
      }
      if (stockSort === 'low') {
        return parseFloat(a.current_stock) - parseFloat(b.current_stock);
      }
      return 0;
    });

  const stockCounts = getStockLevelCounts(filteredStock, settings);
  const totalCurrentStockKg = filteredStock.reduce(
    (sum, item) => sum + (parseFloat(item.current_stock) || 0),
    0
  );
  const formatKg = (value) => {
    if (!Number.isFinite(value)) return '0';
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  };
  const filteredYarnTypes = yarnTypes
    .filter((type) => type.name.toLowerCase().includes(yarnTypeSearch.toLowerCase().trim()))
    .sort((a, b) => {
      if (yarnTypeSort === 'az') {
        return a.name.localeCompare(b.name);
      }
      if (yarnTypeSort === 'za') {
        return b.name.localeCompare(a.name);
      }
      const countA = yarnTypeUsage[String(a.id)] || 0;
      const countB = yarnTypeUsage[String(b.id)] || 0;
      return countB - countA;
    });

  const filteredUnitNumbers = unitNumbers
    .filter((unit) => unit.name.toLowerCase().includes(unitNumberSearch.toLowerCase().trim()))
    .sort((a, b) => {
      if (unitNumberSort === 'az') {
        return a.name.localeCompare(b.name);
      }
      if (unitNumberSort === 'za') {
        return b.name.localeCompare(a.name);
      }
      const countA = unitNumberUsage[String(a.id)] || 0;
      const countB = unitNumberUsage[String(b.id)] || 0;
      return countB - countA;
    });

  const totalTransactionPages = Math.max(1, Math.ceil(transactions.length / transactionsPageSize));
  const safeTransactionsPage = Math.min(transactionsPage, totalTransactionPages);
  const pagedTransactions = transactions.slice(
    (safeTransactionsPage - 1) * transactionsPageSize,
    safeTransactionsPage * transactionsPageSize
  );

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>🏭 Paras Foam {t('Admin Dashboard', 'एडमिन डैशबोर्ड')}</h1>
        <div className="user-info">
          <span>
            {user.username === 'admin' && user.real_name
              ? `${t('Welcome', 'स्वागत है')}, ${user.real_name} 👋`
              : `${t('Welcome', 'स्वागत है')}, ${user.username}`}
          </span>
          <button onClick={onLogout} className="logout-btn">{t('Logout', 'लॉग आउट')}</button>
        </div>
      </header>

      <div className="tabs">
        <button
          className={activeTab === 'add' ? 'active' : ''}
          onClick={() => setActiveTab('add')}
        >
          <span className="tab-icon" aria-hidden="true">➕</span>
          <span className="tab-label">{t('Add Transaction', 'लेनदेन जोड़ें')}</span>
        </button>
        <button
          className={activeTab === 'transactions' ? 'active' : ''}
          onClick={() => setActiveTab('transactions')}
        >
          <span className="tab-icon" aria-hidden="true">📄</span>
          <span className="tab-label">{t('View Transactions', 'लेनदेन देखें')}</span>
        </button>
        <button
          className={activeTab === 'stock' ? 'active' : ''}
          onClick={() => setActiveTab('stock')}
        >
          <span className="tab-icon" aria-hidden="true">📦</span>
          <span className="tab-label">{t('Stock Overview', 'स्टॉक अवलोकन')}</span>
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon" aria-hidden="true">⚙️</span>
          <span className="tab-label">{t('Settings', 'सेटिंग्स')}</span>
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="tab-content">
        {loading ? (
          <div className="loading">{t('Loading...', 'लोड हो रहा है...')}</div>
        ) : (
          <>
            {activeTab === 'add' && (
              <div className="add-transaction-section">
                <h2>{t('Add New Transaction', 'नया लेनदेन जोड़ें')}</h2>
                <form onSubmit={handleSubmit} className="transaction-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="chemical_type_id">{t('Chemical Type', 'रसायन प्रकार')} *</label>
                      <select
                        id="chemical_type_id"
                        name="chemical_type_id"
                        value={formData.chemical_type_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{t('Select Chemical Type', 'रसायन प्रकार चुनें')}</option>
                        {yarnTypes.map((yarnType) => (
                          <option key={yarnType.id} value={yarnType.id}>
                            {yarnType.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="unit_number_id">{t('Unit Number', 'यूनिट नंबर')} *</label>
                      <select
                        id="unit_number_id"
                        name="unit_number_id"
                        value={formData.unit_number_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">{t('Select Unit Number', 'यूनिट नंबर चुनें')}</option>
                        {unitNumbers.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="type">{t('Type', 'प्रकार')} *</label>
                      <select
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="in">{t('Stock In', 'स्टॉक इन')}</option>
                        <option value="out">{t('Stock Out', 'स्टॉक आउट')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="bags">{t('Number of Barrels', 'बैरलों की संख्या')} *</label>
                      <input
                        type="number"
                        id="bags"
                        name="bags"
                        step="1"
                        min="1"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.bags}
                        onChange={handleInputChange}
                        placeholder={t('Enter number of barrels', 'बैरलों की संख्या दर्ज करें')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quantity">{t('Quantity (Kg)', 'मात्रा (किग्रा)')} *</label>
                      <input
                        type="number"
                        id="quantity"
                        name="quantity"
                        step="0.01"
                        min="0.01"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        placeholder={t('Enter quantity in Kg', 'किग्रा में मात्रा दर्ज करें')}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="date">{t('Date', 'तारीख')} *</label>
                      <input
                        type="date"
                        id="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">{t('Notes', 'नोट्स')}</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder={t('Enter any additional notes (optional)', 'अतिरिक्त नोट्स दर्ज करें (वैकल्पिक)')}
                      rows="4"
                    />
                  </div>

                  <button type="submit" className="submit-btn">
                    {t('Add Transaction', 'लेनदेन जोड़ें')}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'stock' && (
              <div className="stock-section">
                <div className="summary-cards">
                  <div className="summary-card">
                    <div className="summary-value">{stockCounts.total}</div>
                    <div className="summary-label">{t('Total SKUs', 'कुल आइटम')}</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-value">{formatKg(totalCurrentStockKg)}</div>
                    <div className="summary-label">{t('Total Stock (Kg)', 'कुल स्टॉक (किग्रा)')}</div>
                  </div>
                  <div className="summary-card summary-low">
                    <div className="summary-value">{stockCounts.low}</div>
                    <div className="summary-label">{t('Low', 'कम')}</div>
                  </div>
                  <div className="summary-card summary-critical">
                    <div className="summary-value">{stockCounts.critical}</div>
                    <div className="summary-label">{t('Critical', 'गंभीर')}</div>
                  </div>
                  <div className="summary-card summary-out">
                    <div className="summary-value">{stockCounts.outOfStock}</div>
                    <div className="summary-label">{t('Out of Stock', 'स्टॉक समाप्त')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
                  <h2>{t('Current Stock Levels', 'वर्तमान स्टॉक स्तर')}</h2>
                  <div className="filters-row">
                    <label htmlFor="yarn-type-filter">{t('Filter by Chemical Type', 'रसायन प्रकार के अनुसार फ़िल्टर करें')}:</label>
                    <select
                      id="yarn-type-filter"
                      value={selectedYarnType}
                      onChange={(e) => setSelectedYarnType(e.target.value)}
                      className="select-input"
                    >
                      <option value="all">{t('All Types', 'सभी प्रकार')}</option>
                      {yarnTypes.map((yarnType) => (
                        <option key={yarnType.id} value={yarnType.id}>
                          {yarnType.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={stockSort}
                      onChange={(e) => setStockSort(e.target.value)}
                      className="select-input"
                    >
                      <option value="none">{t('Sort: Default', 'क्रम: डिफ़ॉल्ट')}</option>
                      <option value="high">{t('Sort: Stock high → low', 'क्रम: स्टॉक अधिक → कम')}</option>
                      <option value="low">{t('Sort: Stock low → high', 'क्रम: स्टॉक कम → अधिक')}</option>
                    </select>
                    <label className="toggle-inline">
                      <input
                        type="checkbox"
                        checked={showTotals}
                        onChange={(e) => setShowTotals(e.target.checked)}
                      />
                      {t('Show In/Out totals', 'इन/आउट कुल दिखाएँ')}
                    </label>
                    <div className="search-field">
                      <input
                        type="text"
                        value={stockSearch}
                        onChange={(e) => setStockSearch(e.target.value)}
                        placeholder={t('Search chemical type...', 'रसायन प्रकार खोजें...')}
                        className="list-search"
                      />
                      {stockSearch && (
                        <button
                          type="button"
                          className="search-clear"
                          onClick={() => setStockSearch('')}
                          aria-label={t('Clear search', 'खोज साफ करें')}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => handleExportStock(filteredStock)}
                      className="btn-edit"
                      style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                      disabled={filteredStock.length === 0}
                    >
                      📥 {t('Export to CSV', 'CSV निर्यात करें')}
                    </button>
                  </div>
                </div>
                {editingStockNote && (
                  <div className="modal-overlay" onClick={() => setEditingStockNote(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Stock Notes', 'स्टॉक नोट्स')} - {editingStockNote.chemical_type_name}</h3>
                      <form onSubmit={handleSaveStockNote}>
                        <div className="form-group">
                          <label>{t('Notes', 'नोट्स')}</label>
                          <textarea
                            value={editingStockNote.notes}
                            onChange={(e) =>
                              setEditingStockNote({
                                ...editingStockNote,
                                notes: e.target.value,
                              })
                            }
                            rows="5"
                            placeholder={t('Add notes about this stock item...', 'इस स्टॉक आइटम के बारे में नोट्स जोड़ें...')}
                          />
                        </div>
                        <div className="modal-actions">
                          <button type="submit">{t('Save', 'सहेजें')}</button>
                          <button type="button" onClick={() => setEditingStockNote(null)}>
                            {t('Cancel', 'रद्द करें')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {filteredStock.length === 0 ? (
                  <div className="empty-state">{t('No stock data available for the current filters.', 'वर्तमान फ़िल्टर के लिए कोई स्टॉक डेटा उपलब्ध नहीं है।')}</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('Chemical Type', 'रसायन प्रकार')}</th>
                      {showTotals && <th>{t('Total Barrels In', 'कुल बैरल इन')}</th>}
                      {showTotals && <th>{t('Total Barrels Out', 'कुल बैरल आउट')}</th>}
                      {showTotals && <th>{t('Total In (Kg)', 'कुल इन (किग्रा)')}</th>}
                      {showTotals && <th>{t('Total Out (Kg)', 'कुल आउट (किग्रा)')}</th>}
                      <th>{t('Current Barrels', 'वर्तमान बैरल')}</th>
                      <th>{t('Current Stock (Kg)', 'वर्तमान स्टॉक (किग्रा)')}</th>
                      <th>{t('Notes', 'नोट्स')}</th>
                    </tr>
                  </thead>
                  <tbody>
                        {filteredStock.map((item, index) => {
                          const colored = settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true;
                          return (
                            <tr key={`${item.chemical_type_id || 'none'}-${index}`}>
                              <td>{item.chemical_type_name || '-'}</td>
                              {showTotals && <td>{item.total_bags_in}</td>}
                              {showTotals && <td>{item.total_bags_out}</td>}
                              {showTotals && <td>{item.total_in}</td>}
                              {showTotals && <td>{item.total_out}</td>}
                              <td>{item.current_bags}</td>
                              <td style={getStockLevelStyle(item.current_stock, settings)}>
                                <div className={`stock-cell ${colored ? 'stock-cell--colored' : 'stock-cell--plain'}`}>
                                  <span className="stock-value">{item.current_stock}</span>
                                </div>
                              </td>
                              <td>
                                <button
                                  onClick={() => handleEditStockNote(item)}
                                  className="btn-edit"
                                  style={{ fontSize: '12px' }}
                                  title={item.stock_notes ? `${t('Notes', 'नोट्स')}: ${item.stock_notes}` : t('Add notes', 'नोट्स जोड़ें')}
                                >
                                  {item.stock_notes ? `📝 ${t('Edit', 'संपादित करें')}` : `➕ ${t('Add Note', 'नोट जोड़ें')}`}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'transactions' && (
              <div className="transactions-section">
                <h2>{t('All Transactions', 'सभी लेनदेन')}</h2>
                {viewingNote && (
                  <div className="modal-overlay" onClick={() => setViewingNote(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Transaction Notes', 'लेनदेन नोट्स')}</h3>
                      <div className="form-group">
                        <p style={{ whiteSpace: 'pre-wrap', padding: '10px', background: '#f5f5f5', borderRadius: '4px', minHeight: '100px' }}>
                          {viewingNote || t('No notes', 'कोई नोट्स नहीं')}
                        </p>
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setViewingNote(null)}>
                          {t('Close', 'बंद करें')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {editingTransaction && (
                  <div className="modal-overlay" onClick={() => setEditingTransaction(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Edit Transaction', 'लेनदेन संपादित करें')}</h3>
                      <form onSubmit={handleUpdateTransaction}>
                        <div className="form-group">
                          <label>{t('Chemical Type', 'रसायन प्रकार')} *</label>
                          <select
                            value={editingTransaction.chemical_type_id || ''}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                chemical_type_id: e.target.value || null,
                              })
                            }
                            required
                          >
                            <option value="">{t('Select Chemical Type', 'रसायन प्रकार चुनें')}</option>
                            {yarnTypes.map((yarnType) => (
                              <option key={yarnType.id} value={yarnType.id}>
                                {yarnType.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('Unit Number', 'यूनिट नंबर')} *</label>
                          <select
                            value={editingTransaction.unit_number_id || ''}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                unit_number_id: e.target.value || null,
                              })
                            }
                            required
                          >
                            <option value="">{t('Select Unit Number', 'यूनिट नंबर चुनें')}</option>
                            {unitNumbers.map((unit) => (
                              <option key={unit.id} value={unit.id}>
                                {unit.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('Type', 'प्रकार')} *</label>
                          <select
                            value={editingTransaction.type}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                type: e.target.value,
                              })
                            }
                            required
                          >
                            <option value="in">{t('In', 'इन')}</option>
                            <option value="out">{t('Out', 'आउट')}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('Number of Barrels', 'बैरलों की संख्या')} *</label>
                          <input
                            type="number"
                            value={editingTransaction.bags || ''}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                bags: e.target.value,
                              })
                            }
                            min="1"
                            step="1"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Quantity (Kg)', 'मात्रा (किग्रा)')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editingTransaction.quantity}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                quantity: e.target.value,
                              })
                            }
                            inputMode="decimal"
                            pattern="[0-9]*[.,]?[0-9]*"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Date', 'तारीख')}</label>
                          <input
                            type="date"
                            value={editingTransaction.date}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                date: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Notes', 'नोट्स')}</label>
                          <textarea
                            value={editingTransaction.notes}
                            onChange={(e) =>
                              setEditingTransaction({
                                ...editingTransaction,
                                notes: e.target.value,
                              })
                            }
                            rows="3"
                          />
                        </div>
                        <div className="modal-actions">
                          <button type="submit">{t('Update', 'अपडेट करें')}</button>
                          <button type="button" onClick={() => setEditingTransaction(null)}>
                            {t('Cancel', 'रद्द करें')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                {transactions.length === 0 ? (
                  <div className="empty-state">{t('No transactions yet. Add a stock movement to get started.', 'अभी तक कोई लेनदेन नहीं है। शुरू करने के लिए स्टॉक मूवमेंट जोड़ें।')}</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                    <tr>
                      <th>{t('Date', 'तारीख')}</th>
                      <th>{t('Chemical Type', 'रसायन प्रकार')}</th>
                      <th>{t('Unit Number', 'यूनिट नंबर')}</th>
                      <th>{t('Type', 'प्रकार')}</th>
                      <th>{t('Barrels', 'बैरल')}</th>
                      <th>{t('Quantity (Kg)', 'मात्रा (किग्रा)')}</th>
                      <th>{t('Notes', 'नोट्स')}</th>
                      <th>{t('Created By', 'बनाने वाला')}</th>
                      <th>{t('Actions', 'क्रियाएँ')}</th>
                    </tr>
                      </thead>
                      <tbody>
                        {pagedTransactions.map((transaction) => (
                          <tr key={transaction.id}>
                            <td>{formatDateOnly(transaction.date, getCurrentTimeZone())}</td>
                            <td>{transaction.chemical_type_name || '-'}</td>
                            <td>{transaction.unit_number_name || '-'}</td>
                        <td>
                          <span className={`badge ${transaction.type}`}>
                            {transaction.type === 'in'
                              ? t('IN', 'इन')
                              : transaction.type === 'out'
                                ? t('OUT', 'आउट')
                                : t('HOLD', 'होल्ड')}
                          </span>
                        </td>
                        <td>{transaction.bags}</td>
                        <td>{transaction.quantity}</td>
                            <td style={{ maxWidth: '200px' }}>
                              {transaction.notes && transaction.notes.length > 50 ? (
                                <>
                                  {transaction.notes.substring(0, 50)}...
                                  <button
                                    onClick={() => setViewingNote(transaction.notes)}
                                    className="btn-edit"
                                    style={{ marginLeft: '5px', fontSize: '11px', padding: '2px 6px' }}
                                  >
                                    {t('Show More', 'और देखें')}
                                  </button>
                                </>
                              ) : (
                                transaction.notes || '-'
                              )}
                            </td>
                            <td>{transaction.created_by_username}</td>
                            <td>
                              <button
                                onClick={() => handleEditTransaction(transaction)}
                                className="btn-edit"
                                disabled={transaction.type === 'hold'}
                              >
                                {t('Edit', 'संपादित करें')}
                              </button>
                              {transaction.type === 'hold' && (
                                <>
                                  <button
                                    onClick={() =>
                                      openConfirmDialog({
                                        type: 'transaction-confirm',
                                        id: transaction.id,
                                        title: t('Hold → Out?', 'होल्ड → आउट?'),
                                        message: t('This will convert the hold into stock out.', 'यह होल्ड को स्टॉक आउट में बदलेगा।')
                                      })
                                    }
                                    className="btn-edit"
                                  >
                                    {t('Hold → Out', 'होल्ड → आउट')}
                                  </button>
                                  <button
                                    onClick={() =>
                                      openConfirmDialog({
                                        type: 'transaction-cancel',
                                        id: transaction.id,
                                        title: t('Refill Stock?', 'स्टॉक वापस करें?'),
                                        message: t('This will cancel the hold and release stock back to available.', 'यह होल्ड रद्द करेगा और स्टॉक को उपलब्ध करेगा।')
                                      })
                                    }
                                    className="btn-edit"
                                  >
                                    {t('Refill Stock', 'स्टॉक वापस करें')}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() =>
                                  openConfirmDialog({
                                    type: 'transaction',
                                    id: transaction.id,
                                    title: t('Delete transaction?', 'लेनदेन हटाएं?'),
                                    message: t(
                                      `This will permanently delete the transaction for ${transaction.chemical_type_name || 'this chemical type'}.`,
                                      `${transaction.chemical_type_name || 'इस रसायन प्रकार'} का लेनदेन स्थायी रूप से हटाया जाएगा।`
                                    )
                                  })
                                }
                                className="btn-delete"
                              >
                                {t('Delete', 'हटाएं')}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {transactions.length > 0 && (
                  <div className="pagination-bar">
                    <div className="pagination-info">
                      {t('Page', 'पृष्ठ')} {safeTransactionsPage} {t('of', 'का')} {totalTransactionPages}
                    </div>
                    <div className="pagination-controls">
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setTransactionsPage((p) => Math.max(1, p - 1))}
                        disabled={safeTransactionsPage === 1}
                      >
                        {t('Previous', 'पिछला')}
                      </button>
                      <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => setTransactionsPage((p) => Math.min(totalTransactionPages, p + 1))}
                        disabled={safeTransactionsPage === totalTransactionPages}
                      >
                        {t('Next', 'अगला')}
                      </button>
                      <select
                        className="page-size-select"
                        value={transactionsPageSize}
                        onChange={(e) => {
                          setTransactionsPageSize(Number(e.target.value));
                          setTransactionsPage(1);
                        }}
                      >
                        <option value={25}>25 / page</option>
                        <option value={50}>50 / page</option>
                        <option value={100}>100 / page</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'settings' && settingsTab === 'yarnTypes' && (
              <div className="settings-shell">
                {renderSettingsNav()}
                <div className="settings-content">
                  <div className="settings-header">
                    <h2>{t('Manage Chemical Types', 'रसायन प्रकार प्रबंधित करें')}</h2>
                    <p className="helper-text">{t('Keep chemical types organized and consistent.', 'रसायन प्रकारों को व्यवस्थित और सुसंगत रखें।')}</p>
                  </div>
                  <div className="list-toolbar">
                  <input
                    type="text"
                    value={yarnTypeSearch}
                    onChange={(e) => setYarnTypeSearch(e.target.value)}
                    placeholder={t('Search chemical types...', 'रसायन प्रकार खोजें...')}
                    className="list-search"
                  />
                  <select
                    value={yarnTypeSort}
                    onChange={(e) => setYarnTypeSort(e.target.value)}
                    className="select-input"
                  >
                    <option value="az">{t('Sort: A–Z', 'क्रम: A–Z')}</option>
                    <option value="za">{t('Sort: Z–A', 'क्रम: Z–A')}</option>
                    <option value="most">{t('Sort: Most used', 'क्रम: सबसे अधिक उपयोग')}</option>
                  </select>
                </div>
                <form onSubmit={handleAddYarnType} className="add-color-form">
                  <input
                    type="text"
                    value={newYarnType}
                    onChange={(e) => setNewYarnType(e.target.value)}
                    placeholder={t('Enter new chemical type name', 'नया रसायन प्रकार दर्ज करें')}
                    required
                  />
                  <button type="submit">{t('Add Chemical Type', 'रसायन प्रकार जोड़ें')}</button>
                </form>
                {filteredYarnTypes.length === 0 ? (
                  <div className="empty-state">{t('No chemical types added yet.', 'अभी तक कोई रसायन प्रकार नहीं जोड़ा गया है।')}</div>
                ) : (
                  <div className="colors-grid">
                    {filteredYarnTypes.map((yarnType) => {
                      const usageCount = yarnTypeUsage[String(yarnType.id)] || 0;
                      const isEditing = editingYarnType && editingYarnType.id === yarnType.id;
                      return (
                      <div key={yarnType.id} className="color-card">
                        <div className="card-main">
                          {isEditing ? (
                            <form onSubmit={handleUpdateYarnType} className="inline-edit">
                              <input
                                type="text"
                                value={editingYarnType.name}
                                onChange={(e) =>
                                  setEditingYarnType({ ...editingYarnType, name: e.target.value })
                                }
                                required
                              />
                              <button type="submit" className="btn-edit">{t('Save', 'सहेजें')}</button>
                              <button
                                type="button"
                                className="btn-delete"
                                onClick={() => setEditingYarnType(null)}
                              >
                                {t('Cancel', 'रद्द करें')}
                              </button>
                            </form>
                          ) : (
                            <>
                              <span>{yarnType.name}</span>
                              <span className="usage-count">{usageCount} {t('used', 'उपयोग')}</span>
                            </>
                          )}
                        </div>
                        {!isEditing && (
                          <div className="card-actions">
                            <button
                              onClick={() => handleEditYarnType(yarnType)}
                              className="btn-edit"
                              title={t('Rename', 'नाम बदलें')}
                            >
                              ✎
                            </button>
                            <button
                              onClick={() =>
                                openConfirmDialog({
                                  type: 'yarnType',
                                  id: yarnType.id,
                                  title: t('Delete chemical type?', 'रसायन प्रकार हटाएं?'),
                                  message: t(`This will permanently delete "${yarnType.name}".`, `"${yarnType.name}" को स्थायी रूप से हटाया जाएगा।`)
                                })
                              }
                              className="btn-delete-small"
                              disabled={usageCount > 0}
                              title={usageCount > 0 ? t('Cannot delete: chemical type is in use', 'हटाया नहीं जा सकता: रसायन प्रकार उपयोग में है') : t('Delete chemical type', 'रसायन प्रकार हटाएं')}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && settingsTab === 'unitNumbers' && (
              <div className="settings-shell">
                {renderSettingsNav()}
                <div className="settings-content">
                  <div className="settings-header">
                    <h2>{t('Manage Unit Numbers', 'यूनिट नंबर प्रबंधित करें')}</h2>
                    <p className="helper-text">{t('Keep unit numbers organized and consistent.', 'यूनिट नंबर व्यवस्थित और सुसंगत रखें।')}</p>
                  </div>
                  <div className="list-toolbar">
                    <input
                      type="text"
                      value={unitNumberSearch}
                      onChange={(e) => setUnitNumberSearch(e.target.value)}
                      placeholder={t('Search unit numbers...', 'यूनिट नंबर खोजें...')}
                      className="list-search"
                    />
                    <select
                      value={unitNumberSort}
                      onChange={(e) => setUnitNumberSort(e.target.value)}
                      className="select-input"
                    >
                      <option value="az">{t('Sort: A–Z', 'क्रम: A–Z')}</option>
                      <option value="za">{t('Sort: Z–A', 'क्रम: Z–A')}</option>
                      <option value="most">{t('Sort: Most used', 'क्रम: सबसे अधिक उपयोग')}</option>
                    </select>
                  </div>
                  <form onSubmit={handleAddUnitNumber} className="add-color-form">
                    <input
                      type="text"
                      value={newUnitNumber}
                      onChange={(e) => setNewUnitNumber(e.target.value)}
                      placeholder={t('Enter new unit number', 'नया यूनिट नंबर दर्ज करें')}
                      required
                    />
                    <button type="submit">{t('Add Unit Number', 'यूनिट नंबर जोड़ें')}</button>
                  </form>
                  {filteredUnitNumbers.length === 0 ? (
                    <div className="empty-state">{t('No unit numbers added yet.', 'अभी तक कोई यूनिट नंबर नहीं जोड़ा गया है।')}</div>
                  ) : (
                    <div className="colors-grid">
                      {filteredUnitNumbers.map((unit) => {
                        const usageCount = unitNumberUsage[String(unit.id)] || 0;
                        const isEditing = editingUnitNumber && editingUnitNumber.id === unit.id;
                        return (
                          <div key={unit.id} className="color-card">
                            <div className="card-main">
                              {isEditing ? (
                                <form onSubmit={handleUpdateUnitNumber} className="inline-edit">
                                  <input
                                    type="text"
                                    value={editingUnitNumber.name}
                                    onChange={(e) =>
                                      setEditingUnitNumber({ ...editingUnitNumber, name: e.target.value })
                                    }
                                    required
                                  />
                                  <button type="submit" className="btn-edit">{t('Save', 'सहेजें')}</button>
                                  <button
                                    type="button"
                                    className="btn-delete"
                                    onClick={() => setEditingUnitNumber(null)}
                                  >
                                    {t('Cancel', 'रद्द करें')}
                                  </button>
                                </form>
                              ) : (
                                <>
                                  <span>{unit.name}</span>
                                  <span className="usage-count">{usageCount} {t('used', 'उपयोग')}</span>
                                </>
                              )}
                            </div>
                            {!isEditing && (
                              <div className="card-actions">
                                <button
                                  onClick={() => handleEditUnitNumber(unit)}
                                  className="btn-edit"
                                  title={t('Rename', 'नाम बदलें')}
                                >
                                  ✎
                                </button>
                                <button
                                  onClick={() =>
                                    openConfirmDialog({
                                      type: 'unitNumber',
                                      id: unit.id,
                                      title: t('Delete unit number?', 'यूनिट नंबर हटाएं?'),
                                      message: t(`This will permanently delete "${unit.name}".`, `"${unit.name}" को स्थायी रूप से हटाया जाएगा।`)
                                    })
                                  }
                                  className="btn-delete-small"
                                  disabled={usageCount > 0}
                                  title={usageCount > 0 ? t('Cannot delete: unit number is in use', 'हटाया नहीं जा सकता: यूनिट नंबर उपयोग में है') : t('Delete unit number', 'यूनिट नंबर हटाएं')}
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'settings' && settingsTab === 'system' && (
              <div className="settings-shell">
                {renderSettingsNav()}
                <div className="settings-content">
                  <div className="settings-header">
                    <h2>⚙️ {t('System Settings', 'सिस्टम सेटिंग्स')}</h2>
                    <p className="helper-text">{t('Tune thresholds and defaults for stock health.', 'स्टॉक स्वास्थ्य के लिए थ्रेशहोल्ड और डिफ़ॉल्ट सेट करें।')}</p>
                  </div>
                  {(() => {
                  const colored = settings.color_indicators_enabled === 'true' || settings.color_indicators_enabled === true;
                  const critical = parseFloat(settings.stock_threshold_critical);
                  const low = parseFloat(settings.stock_threshold_low);
                  const medium = parseFloat(settings.stock_threshold_medium);
                  const validNumbers = !isNaN(critical) && !isNaN(low) && !isNaN(medium);
                  const validOrder = validNumbers && critical < low && low < medium;
                  const dirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);

                  const handleSave = async () => {
                    if (!validOrder) return;
                    try {
                      await api.updateSettings(settings);
                      setSuccess(t('Settings updated successfully!', 'सेटिंग्स सफलतापूर्वक अपडेट हुईं!'));
                      setSavedSettings(settings);
                      loadData();
                    } catch (err) {
                      setError(err.message);
                    }
                  };

                  return (
                    <div className="settings-grid">
                      <div className="section-card">
                        <h3>{t('Display', 'डिस्प्ले')}</h3>
                        <p className="helper-text">{t('Control how stock levels appear across the app.', 'पूरे ऐप में स्टॉक स्तर कैसे दिखें, नियंत्रित करें।')}</p>
                        <div className="toggle-row">
                          <label className="toggle-label">
                            <input
                              type="checkbox"
                              checked={colored}
                              onChange={(e) => setSettings({ ...settings, color_indicators_enabled: e.target.checked })}
                            />
                            <span>
                              <strong>{t('Enable color indicators', 'रंग संकेत सक्षम करें')}</strong>
                              <small>{colored ? t('Stock levels are color-coded based on thresholds.', 'स्टॉक स्तर थ्रेशहोल्ड के आधार पर रंग-कोडेड हैं।') : t('Stock levels are shown in plain text.', 'स्टॉक स्तर सादे टेक्स्ट में दिखते हैं।')}</small>
                            </span>
                          </label>
                        </div>
                        <div className="tooltip-row">
                          <div className="tooltip">
                            {t('What do these colors mean?', 'इन रंगों का क्या अर्थ है?')}
                            <div className="tooltip-content">
                              <div className="legend-item">
                                <span className="legend-swatch legend-critical" /> {t('Critical', 'गंभीर')} (≤ critical)
                              </div>
                              <div className="legend-item">
                                <span className="legend-swatch legend-low" /> {t('Low', 'कम')} (≤ low)
                              </div>
                              <div className="legend-item">
                                <span className="legend-swatch legend-medium" /> {t('Medium', 'मध्यम')} (≤ medium)
                              </div>
                              <div className="legend-item">
                                <span className="legend-swatch legend-high" /> {t('High', 'उच्च')} (&gt; medium)
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="section-card">
                        <h3>{t('Stock Thresholds (Kg)', 'स्टॉक थ्रेशहोल्ड (किग्रा)')}</h3>
                        <p className="helper-text">{t('Set the levels that determine color indicators.', 'रंग संकेत निर्धारित करने वाले स्तर सेट करें।')}</p>

                        <div className="threshold-row">
                          <div className="threshold-label">
                            <strong>{t('Critical', 'गंभीर')}</strong>
                            <small>{t('Stock at or below this level is critical.', 'इस स्तर या उससे नीचे स्टॉक गंभीर है।')}</small>
                          </div>
                          <div className="threshold-inputs">
                            <input
                              type="range"
                              min="0"
                              max="1000"
                              step="1"
                              value={settings.stock_threshold_critical}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_critical: e.target.value })}
                            />
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={settings.stock_threshold_critical}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_critical: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="threshold-row">
                          <div className="threshold-label">
                            <strong>{t('Low', 'कम')}</strong>
                            <small>{t('Stock at or below this level is low.', 'इस स्तर या उससे नीचे स्टॉक कम है।')}</small>
                          </div>
                          <div className="threshold-inputs">
                            <input
                              type="range"
                              min="0"
                              max="1000"
                              step="1"
                              value={settings.stock_threshold_low}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_low: e.target.value })}
                            />
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={settings.stock_threshold_low}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_low: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className="threshold-row">
                          <div className="threshold-label">
                            <strong>{t('Medium', 'मध्यम')}</strong>
                            <small>{t('Stock at or below this level is good.', 'इस स्तर या उससे नीचे स्टॉक ठीक है।')}</small>
                          </div>
                          <div className="threshold-inputs">
                            <input
                              type="range"
                              min="0"
                              max="1000"
                              step="1"
                              value={settings.stock_threshold_medium}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_medium: e.target.value })}
                            />
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={settings.stock_threshold_medium}
                              onChange={(e) => setSettings({ ...settings, stock_threshold_medium: e.target.value })}
                            />
                          </div>
                        </div>

                        <div className={`validation-row ${validOrder ? 'valid' : 'invalid'}`}>
                          {validOrder
                            ? t('✓ Thresholds look good (Critical < Low < Medium).', '✓ थ्रेशहोल्ड सही हैं (गंभीर < कम < मध्यम)।')
                            : t('✕ Fix order: Critical must be lower than Low, and Low lower than Medium.', '✕ क्रम सही करें: गंभीर < कम < मध्यम होना चाहिए।')}
                        </div>

                        <div className="section-card setting-preview">
                          <strong>{t('Current Values', 'वर्तमान मान')}</strong>
                          <ul>
                            <li>{t('Critical', 'गंभीर')}: ≤ {settings.stock_threshold_critical} Kg</li>
                            <li>{t('Low', 'कम')}: ≤ {settings.stock_threshold_low} Kg</li>
                            <li>{t('Medium', 'मध्यम')}: ≤ {settings.stock_threshold_medium} Kg</li>
                            <li>{t('High', 'उच्च')}: &gt; {settings.stock_threshold_medium} Kg</li>
                          </ul>
                        </div>
                      </div>

                      <div className="section-card">
                        <h3>{t('Defaults', 'डिफ़ॉल्ट')}</h3>
                        <p className="helper-text">{t('Reset to factory defaults if settings drift.', 'यदि सेटिंग्स बदल जाएँ तो डिफ़ॉल्ट पर रीसेट करें।')}</p>
                        <div className="defaults-box">
                          <div>{t('Critical', 'गंभीर')}: {DEFAULT_SETTINGS.stock_threshold_critical} Kg</div>
                          <div>{t('Low', 'कम')}: {DEFAULT_SETTINGS.stock_threshold_low} Kg</div>
                          <div>{t('Medium', 'मध्यम')}: {DEFAULT_SETTINGS.stock_threshold_medium} Kg</div>
                          <div>{t('Color indicators', 'रंग संकेत')}: {DEFAULT_SETTINGS.color_indicators_enabled}</div>
                        </div>
                        <button
                          type="button"
                          className="btn-delete"
                          onClick={() => {
                            if (!window.confirm(t('Reset settings to defaults?', 'सेटिंग्स को डिफ़ॉल्ट पर रीसेट करें?'))) return;
                            setSettings(DEFAULT_SETTINGS);
                          }}
                        >
                          {t('Reset to Defaults', 'डिफ़ॉल्ट पर रीसेट करें')}
                        </button>
                      </div>

                      {dirty && (
                        <div className="settings-savebar">
                          <span>{t('Unsaved changes', 'असहेजे परिवर्तन')}</span>
                          <div className="savebar-actions">
                            <button
                              type="button"
                              className="btn-delete"
                              onClick={() => setSettings(savedSettings)}
                            >
                              {t('Discard', 'त्यागें')}
                            </button>
                            <button
                              type="button"
                              className="btn-edit"
                              disabled={!validOrder}
                              onClick={handleSave}
                            >
                              {t('Save Changes', 'परिवर्तन सहेजें')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })()}
                </div>
              </div>
            )}

            {activeTab === 'settings' && settingsTab === 'users' && (
              <div className="settings-shell">
                {renderSettingsNav()}
                <div className="settings-content">
                  <div className="settings-header">
                    <h2>{t('Manage Users', 'उपयोगकर्ता प्रबंधित करें')}</h2>
                    <p className="helper-text">{t('Control access, roles, and timezones.', 'एक्सेस, भूमिकाएँ और टाइमज़ोन नियंत्रित करें।')}</p>
                  </div>
                  {editingUser && (
                  <div className="modal-overlay" onClick={() => setEditingUser(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Edit User', 'उपयोगकर्ता संपादित करें')}</h3>
                      <form onSubmit={handleUpdateUser}>
                        <div className="form-group">
                          <label>{t('Username', 'यूज़रनेम')} *</label>
                          <input
                            type="text"
                            value={editingUser.username}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                username: e.target.value,
                              })
                            }
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('New Password (leave empty to keep current)', 'नया पासवर्ड (वर्तमान रखने के लिए खाली छोड़ें)')}</label>
                          <input
                            type="password"
                            value={editingUser.password}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                password: e.target.value,
                              })
                            }
                            placeholder={t('Enter new password or leave empty', 'नया पासवर्ड दर्ज करें या खाली छोड़ें')}
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Role', 'भूमिका')} *</label>
                          <select
                            value={editingUser.role}
                            onChange={(e) =>
                              setEditingUser({
                                ...editingUser,
                                role: e.target.value,
                              })
                            }
                            required
                          >
                            <option value="user">{t('User', 'यूज़र')}</option>
                            <option value="admin">{t('Admin', 'एडमिन')}</option>
                          </select>
                        </div>
                        <div className="modal-actions">
                          <button type="submit">{t('Update', 'अपडेट करें')}</button>
                          <button type="button" onClick={() => setEditingUser(null)}>
                            {t('Cancel', 'रद्द करें')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                <div className="users-toolbar">
                  <button
                    type="button"
                    className="btn-edit"
                    onClick={() => setShowCreateUser(true)}
                  >
                    + {t('Create User', 'उपयोगकर्ता बनाएं')}
                  </button>
                </div>
                {showCreateUser && (
                  <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                      <h3>{t('Create User', 'उपयोगकर्ता बनाएं')}</h3>
                      <form
                        onSubmit={(e) => {
                          handleAddUser(e);
                          setShowCreateUser(false);
                        }}
                      >
                        <div className="form-group">
                          <label>{t('Username', 'यूज़रनेम')} *</label>
                          <input
                            type="text"
                            value={newUser.username}
                            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                            placeholder={t('Username', 'यूज़रनेम')}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Password', 'पासवर्ड')} *</label>
                          <input
                            type="password"
                            value={newUser.password}
                            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                            placeholder={t('Password', 'पासवर्ड')}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>{t('Role', 'भूमिका')}</label>
                          <select
                            value={newUser.role}
                            onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                          >
                            <option value="user">{t('User', 'यूज़र')}</option>
                            <option value="admin">{t('Admin', 'एडमिन')}</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>{t('Language', 'भाषा')}</label>
                          <select
                            value={newUser.language || 'en'}
                            onChange={(e) => setNewUser({ ...newUser, language: e.target.value })}
                          >
                            <option value="en">{t('English', 'अंग्रेज़ी')}</option>
                            <option value="hi">{t('Hindi', 'हिंदी')}</option>
                          </select>
                        </div>
                        <div className="modal-actions">
                          <button type="submit">{t('Create User', 'उपयोगकर्ता बनाएं')}</button>
                          <button
                            type="button"
                            onClick={() => setShowCreateUser(false)}
                          >
                            {t('Cancel', 'रद्द करें')}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
                  {users.length === 0 ? (
                  <div className="empty-state">{t('No users found.', 'कोई उपयोगकर्ता नहीं मिला।')}</div>
                ) : (
                  <div className="table-wrapper">
                    <table className="data-table" style={{ marginTop: '20px' }}>
                      <thead>
                        <tr>
                          <th>{t('Username', 'यूज़रनेम')}</th>
                          <th>{t('Role', 'भूमिका')}</th>
                          <th>{t('Language', 'भाषा')}</th>
                          <th>{t('Status', 'स्थिति')}</th>
                          <th>{t('Timezone', 'टाइमज़ोन')}</th>
                          <th>{t('Last Login', 'आख़िरी लॉगिन')}</th>
                          <th>{t('Created At', 'बनाया गया')}</th>
                          <th>{t('Actions', 'क्रियाएँ')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((usr) => {
                          const lockedUntil = usr.locked_until ? new Date(usr.locked_until) : null;
                          const isLocked = lockedUntil && !isNaN(lockedUntil.getTime()) && lockedUntil > new Date();
                          const timeZone = usr.timezone || (usr.username === 'admin' ? 'America/Chicago' : 'Asia/Kolkata');
                          const lastLoginDate = parseUtcTimestamp(usr.last_login_at);
                          const lastLogin = lastLoginDate
                            ? new Intl.DateTimeFormat('en-US', {
                                timeZone,
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              }).format(lastLoginDate)
                            : t('Never Logged In', 'कभी लॉगिन नहीं किया');
                          const createdAt = formatDateOnly(usr.created_at, timeZone);
                          return (
                          <tr key={usr.id}>
                            <td>{usr.username}</td>
                            <td>
                              <span className={`badge ${usr.role === 'admin' ? 'in' : 'out'}`}>
                                {usr.role === 'admin' ? t('ADMIN', 'एडमिन') : t('USER', 'यूज़र')}
                              </span>
                            </td>
                            <td>
                              {user.role === 'admin' ? (
                                <select
                                  className="select-input compact"
                                  value={usr.language || 'en'}
                                  onChange={(e) => handleLanguageChange(usr.id, e.target.value)}
                                >
                                  <option value="en">{t('English', 'अंग्रेज़ी')}</option>
                                  <option value="hi">{t('Hindi', 'हिंदी')}</option>
                                </select>
                              ) : (
                                <span>{usr.language === 'hi' ? t('Hindi', 'हिंदी') : t('English', 'अंग्रेज़ी')}</span>
                              )}
                            </td>
                            <td>
                              {isLocked ? (
                                <span className="badge out">{t('LOCKED', 'लॉक्ड')}</span>
                              ) : (
                                <span className="badge in">{t('ACTIVE', 'सक्रिय')}</span>
                              )}
                            </td>
                            <td>
                              {user.username === 'admin' ? (
                                <select
                                  className="select-input compact"
                                  value={timeZone}
                                  onChange={(e) => handleTimezoneChange(usr.id, e.target.value)}
                                >
                                  <option value="Asia/Kolkata">IST</option>
                                  <option value="America/Chicago">CST</option>
                                </select>
                              ) : (
                                <span>{timeZone === 'America/Chicago' ? 'CST' : 'IST'}</span>
                              )}
                            </td>
                            <td>{lastLogin}</td>
                            <td>{createdAt}</td>
                            <td>
                              <button
                                onClick={() => handleEditUser(usr)}
                                className="btn-edit"
                                disabled={usr.id === user.id}
                              >
                                {t('Edit', 'संपादित करें')}
                              </button>
                              {user.username === 'admin' && isLocked && (
                                <button
                                  onClick={() =>
                                    openConfirmDialog({
                                      type: 'unlock',
                                      id: usr.username,
                                      title: t('Unlock user?', 'यूज़र अनलॉक करें?'),
                                      message: t(`This will remove the lock for "${usr.username}".`, `"${usr.username}" के लिए लॉक हटा दिया जाएगा।`)
                                    })
                                  }
                                  className="btn-edit"
                                >
                                  {t('Unlock', 'अनलॉक')}
                                </button>
                              )}
                              <button
                                onClick={() =>
                                  openConfirmDialog({
                                    type: 'user',
                                    id: usr.id,
                                    title: t('Delete user?', 'यूज़र हटाएं?'),
                                    message: t(`This will permanently delete "${usr.username}".`, `"${usr.username}" को स्थायी रूप से हटाया जाएगा।`)
                                  })
                                }
                                className="btn-delete"
                                disabled={usr.id === user.id || usr.username === 'admin'}
                              >
                                {t('Delete', 'हटाएं')}
                              </button>
                            </td>
                          </tr>
                        )})}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {confirmDialog && (
        <div className="modal-overlay" onClick={closeConfirmDialog}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirmDialog.title || t('Confirm action', 'कृपया पुष्टि करें')}</h3>
            <p className="modal-text">{confirmDialog.message || t('Are you sure you want to proceed?', 'क्या आप आगे बढ़ना चाहते हैं?')}</p>
            <div className="modal-actions">
              <button type="button" onClick={closeConfirmDialog}>
                {t('Cancel', 'रद्द करें')}
              </button>
              <button type="button" className="btn-delete modal-delete" onClick={executeConfirmAction}>
                {confirmDialog.type === 'transaction-confirm'
                  ? t('Hold → Out', 'होल्ड → आउट')
                  : confirmDialog.type === 'transaction-cancel'
                    ? t('Refill Stock', 'स्टॉक वापस करें')
                    : confirmDialog.type === 'unlock'
                      ? t('Unlock', 'अनलॉक')
                      : t('Delete', 'हटाएं')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
